import React, { useState, useEffect } from "react";
import { useRouteMatch } from "react-router-dom";
import { Row, Col } from "reactstrap";
import _ from "lodash";
import "./index.css";
import ApiCaller from "../../../api/apiCaller";
import CompetitorSelectionModal from "../components/CompetitorSelectionModal";
import SiteInBuildingRentalCalculationModal from "../../Site/components/SiteInBuildingRentalCalculationModal";
import SiteWholeRentalCalculationModal from "../../Site/components/SiteWholeRentalCalculationModal";
import SiteRevenueShareRentalCalculationModal from "../../Site/components/SiteRevenueShareRentalCalculationModal";
import SiteComparisonControl from "../containers/SiteComparisonControl";
import SiteComparisonSelection from "../containers/SiteComparisonSelection";
import SiteComparisonTable from "../containers/SiteComparisonTable";

interface SiteComparisonProps {
  siteIdInput?: number;
  viewOnly?: boolean;
}

export default function SiteComparison({
  siteIdInput,
  viewOnly,
}: SiteComparisonProps) {
  const match = useRouteMatch("/sitev2/:site_id");
  const params: any = match?.params;
  const siteId = siteIdInput || params?.site_id;

  const [comparisonMetrics, setComparisonMetrics] = useState<any | null>(null);
  const [metricLabels, setMetricLabels] = useState<any | null>({});
  const [siteNameKeywords, setSiteNameKeywords] = useState<string>("");
  const [storeNameKeywords, setStoreNameKeywords] = useState<string>("");
  const [sites, setSites] = useState<Array<any>>([]);
  const [stores, setStores] = useState<Array<any>>([]);
  const [siteIdsToCompare, setSiteIdsToCompare] = useState<Array<any>>([]);
  const [storeIdsToCompare, setStoreIdsToCompare] = useState<Array<any>>([]);
  const [locationIdsToCompare, setLocationIdsToCompare] = useState<Array<any>>(
    []
  );
  const [showSelectionModal, setShowSelectionModal] = useState<boolean>(false);
  const [
    showCompetitorSelectionModal,
    setShowCompetitorSelectionModal,
  ] = useState<boolean>(false);
  const [canFetchNewComparison, setCanFetchNewComparison] = useState<boolean>(
    false
  );

  const [rentalCalculationMode, setRentalCalculationMode] = useState<string>(
    ""
  );
  const [showRentalCalculationModal, setShowRentalCalculationModal] = useState<
    boolean
  >(false);
  const [
    rentalCalculationModalSiteId,
    setRentalCalculationModalSiteId,
  ] = useState<number>(0);

  const getSiteListing = async () => {
    if (!siteNameKeywords) return;
    let uriParams = `page=0&limit=10&order_by=created_on&order_direction=desc&name=${siteNameKeywords}`;
    const res = await ApiCaller.get(`site/list?${uriParams}`);
    if (res?.result) {
      setSites(res.result);
    }
  };
  const getStoreListing = async () => {
    if (!storeNameKeywords) return;
    let uriParams = `page=0&limit=10&order_by=created_on&order_direction=desc&name=${storeNameKeywords}`;
    const res = await ApiCaller.get(`store/list_v2?${uriParams}`);
    if (res?.result) {
      setStores(res.result);
    }
  };
  const debounceSiteNameKeywords = useDebounce(siteNameKeywords, 300);
  const debounceStoreNameKeywords = useDebounce(storeNameKeywords, 300);

  const addSiteForComparison = (site: any) => {
    setSiteIdsToCompare(_.uniq([...siteIdsToCompare, site.id]));
    setCanFetchNewComparison(true);
  };

  const addStoreForComparison = (store: any) => {
    setStoreIdsToCompare(_.uniq([...storeIdsToCompare, store.id]));
    setCanFetchNewComparison(true);
  };

  const addLocationForComparison = (item: any) => {
    setLocationIdsToCompare(_.uniq([...locationIdsToCompare, item.id]));
    setCanFetchNewComparison(true);
  };

  const getComparisonHistory = async () => {
    const res = await ApiCaller.get(`site_comparison_history/${siteId}/latest`);
    if (res.status === "ok") {
      if (res.result) {
        const comparisonHistory = res.result;
        setSiteIdsToCompare(comparisonHistory.site_ids_to_compare);
        setStoreIdsToCompare(comparisonHistory.store_ids_to_compare);
        setLocationIdsToCompare(comparisonHistory.location_ids_to_compare);
        setComparisonMetrics(comparisonHistory.comparison_result_json);
        setMetricLabels(res._labels);
      } else {
        // If no comparison is found
        // Fetch new comparison
        getComparisonMetrics();
        setCanFetchNewComparison(true);
      }
    }
  };

  const saveCurrentComparisonHistory = async () => {
    const saveData = {
      site_ids_to_compare: siteIdsToCompare,
      store_ids_to_compare: storeIdsToCompare,
      location_ids_to_compare: locationIdsToCompare,
      comparison_result: comparisonMetrics,
    };
    const res = await ApiCaller.post(
      `site_comparison_history/${siteId}/save`,
      saveData
    );
    if (res.status === "ok") {
      alert("Đã lưu");
    }
  };

  const getComparisonMetrics = async () => {
    const siteIdsString = _.uniq(siteIdsToCompare).join();
    const storeIdsString = _.uniq(storeIdsToCompare).join();
    const locationIdsString = _.uniq(locationIdsToCompare).join();
    if (!siteId && !siteIdsString && !storeIdsString) return;
    let uri = `site/comparison?site_ids=${siteId},${siteIdsString}&store_ids=${storeIdsString}`;

    if (locationIdsString) {
      uri = `${uri}&location_ids=${locationIdsString}`;
    }
    const res = await ApiCaller.get(uri);
    if (res?.result) {
      setComparisonMetrics(res.result);
      setMetricLabels(res._labels);
      setShowSelectionModal(false);
      // Reset search boxes and their result
      setStoreNameKeywords("");
      setStores([]);
      setSiteNameKeywords("");
      setSites([]);
    }
  };

  const removeElementFromComparison = (element: any) => () => {
    if (element.type === "site") {
      const newSiteIdsToCompare = siteIdsToCompare.filter(
        (checkSiteId) => checkSiteId !== element.id
      );
      setSiteIdsToCompare(newSiteIdsToCompare);
      setCanFetchNewComparison(true);
    } else if (element.type === "store") {
      const newStoreIdsToCompare = storeIdsToCompare.filter(
        (checkStoreId) => checkStoreId !== element.id
      );
      setStoreIdsToCompare(newStoreIdsToCompare);
      setCanFetchNewComparison(true);
    } else if (element.type === "location") {
      const newLocationIdsToCompare = locationIdsToCompare.filter(
        (checkLocationId) => checkLocationId !== element.id
      );
      setLocationIdsToCompare(newLocationIdsToCompare);
      setCanFetchNewComparison(true);
    }
  };

  const getNearbyAndSisterStore = async () => {
    const getSisterSiteRes = await ApiCaller.get(`site/${siteId}/sister_store`);

    setCanFetchNewComparison(true);
    const storeIdsToFetch = [];
    if (getSisterSiteRes.status === "ok") {
      const sisterSiteId = getSisterSiteRes.sister_site_id;
      if (sisterSiteId) {
        storeIdsToFetch.push(sisterSiteId);
      }
    }

    const getNearbyStoreRes = await ApiCaller.get(
      `site/${siteId}/nearby/?same_brand=1&radius=1`
    );
    if (
      getNearbyStoreRes.nearby_locations &&
      getNearbyStoreRes.nearby_locations.length > 0
    ) {
      const nearbyStore = getNearbyStoreRes.nearby_locations[0];
      if (nearbyStore) {
        storeIdsToFetch.push(nearbyStore.store_id);
      }
    } else {
      alert("Không có cửa hàng nào gần đây");
    }

    setStoreIdsToCompare(_.uniq([...storeIdsToCompare, ...storeIdsToFetch]));
  };

  const toggleSelectionModal = () => {
    setShowSelectionModal(!showSelectionModal);
  };

  const toggleCompetitorSelectionModal = () => {
    setShowCompetitorSelectionModal(!showCompetitorSelectionModal);
  };

  const toggleRentalCalculationModal = (
    calculationMethod: string,
    siteId: number
  ) => () => {
    setShowRentalCalculationModal(true);
    setRentalCalculationModalSiteId(siteId);
    setRentalCalculationMode(calculationMethod);
  };

  const siteNameKeywordsHandler = (event: any) => {
    setSiteNameKeywords(event.target.value);
  };
  useEffect(() => {
    getSiteListing();
    setStoreNameKeywords("");
    setStores([]);
  }, [debounceSiteNameKeywords]);

  const storeNameKeywordsHandler = (event: any) => {
    setStoreNameKeywords(event.target.value);
  };
  useEffect(() => {
    getStoreListing();
    setSiteNameKeywords("");
    setSites([]);
  }, [debounceStoreNameKeywords]);

  useEffect(() => {
    // On load, get comparison history
    // If no history is found, fetch a new comparison
    getComparisonHistory();
  }, []);

  useEffect(() => {
    if (canFetchNewComparison) {
      getComparisonMetrics();
    }
  }, [
    siteIdsToCompare,
    storeIdsToCompare,
    locationIdsToCompare,
    canFetchNewComparison,
  ]);

  return (
    <div
      className={`site-comparison__main-container-${
        viewOnly ? "viewOnly" : "editOnly"
      }`}
    >
      {!viewOnly && (
        <SiteComparisonControl
          getNearbyAndSisterStore={getNearbyAndSisterStore}
          saveCurrentComparisonHistory={saveCurrentComparisonHistory}
          toggleCompetitorSelectionModal={toggleCompetitorSelectionModal}
        />
      )}
      <SiteComparisonSelection
        addSiteForComparison={addSiteForComparison}
        addStoreForComparison={addStoreForComparison}
        showSelectionModal={showSelectionModal}
        siteNameKeywords={siteNameKeywords}
        siteNameKeywordsHandler={siteNameKeywordsHandler}
        storeNameKeywords={storeNameKeywords}
        storeNameKeywordsHandler={storeNameKeywordsHandler}
        toggleCompetitorSelectionModal={toggleCompetitorSelectionModal}
        toggleSelectionModal={toggleSelectionModal}
        sites={sites}
        stores={stores}
      />
      {showCompetitorSelectionModal && (
        <CompetitorSelectionModal
          isShow={showCompetitorSelectionModal || true}
          siteId={siteId}
          onItemSelected={(item: any) => addLocationForComparison(item)}
        />
      )}
      {comparisonMetrics && comparisonMetrics.length > 0 && (
        <Row>
          <Col xs={12}>
            <SiteComparisonTable
              metricLabels={metricLabels}
              removeElementFromComparison={removeElementFromComparison}
              siteId={siteId}
              toggleSelectionModal={toggleSelectionModal}
              viewOnly={viewOnly}
              toggleRentalCalculationModal={toggleRentalCalculationModal}
              comparisonMetrics={comparisonMetrics}
              siteIdsToCompare={siteIdsToCompare}
              storeIdsToCompare={storeIdsToCompare}
            />
          </Col>
        </Row>
      )}
      {rentalCalculationMode === "area" && showRentalCalculationModal && (
        <SiteInBuildingRentalCalculationModal
          siteId={rentalCalculationModalSiteId}
          isView
          isShow={showRentalCalculationModal}
          onCancelCallback={() => setShowRentalCalculationModal(false)}
        />
      )}
      {rentalCalculationMode === "whole" && showRentalCalculationModal && (
        <SiteWholeRentalCalculationModal
          siteId={rentalCalculationModalSiteId}
          isView
          isShow={showRentalCalculationModal}
          onCancelCallback={() => setShowRentalCalculationModal(false)}
        />
      )}
      {rentalCalculationMode === "revenue_share" &&
        showRentalCalculationModal && (
          <SiteRevenueShareRentalCalculationModal
            siteId={rentalCalculationModalSiteId}
            isView
            isShow={showRentalCalculationModal}
            onCancelCallback={() => setShowRentalCalculationModal(false)}
          />
        )}
    </div>
  );
}

function useDebounce(value: any, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}
